import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Tear off one layer of asphalt shingles, 24 squares, 6/12 pitch; dumpster and haul-off', amount: '$3,100' },
  { item: 'Synthetic underlayment, ice and water shield at eaves and valleys, new drip edge', amount: '$2,050' },
  { item: 'Architectural shingles, 24 squares plus starter and ridge cap; new pipe boots and step flashing', amount: '$7,900' },
  { item: 'Decking replacement allowance: up to 4 sheets of 7/16 OSB included; more priced per sheet', amount: '$360' },
  { item: 'Optional: continuous ridge vent in place of the four box vents', amount: '$540' },
];

const faq = [
  {
    q: 'How do roofers price a replacement?',
    a: 'By the square, which is 100 square feet of roof surface, adjusted for pitch, the number of layers to tear off, valleys, penetrations and access. Underlayment, flashing, ventilation and disposal are usually listed as their own lines so the homeowner can compare quotes properly.',
  },
  {
    q: 'How should a quote handle rotten decking we cannot see yet?',
    a: 'Include an allowance for a set number of sheets in the price and state the per-sheet charge for anything beyond it. The homeowner agrees to the rule before tear-off, so there is no argument when the old shingles come up.',
  },
  {
    q: 'Does Scopie handle insurance claims or supplements?',
    a: 'No. Scopie is built for quotes the homeowner pays for: repairs and replacements priced, signed, scheduled and invoiced. It has no insurance claim, adjuster or supplement workflow, and no aerial measurement.',
  },
  {
    q: 'Can the homeowner choose upgrades on the quote?',
    a: 'Yes. Put upgrades such as a ridge vent, a better shingle line or new gutters as optional lines. The homeowner ticks the ones they want on the emailed quote and the total updates before they sign.',
  },
];

const page: IndustryPage = {
  slug: 'roofing',
  order: 19,
  group: 'Building and remodeling',
  path: '/for/roofing',
  primaryKeyword: 'roofing estimate software',
  secondaryKeywords: ['roofing estimate template', 'roof replacement quote', 'roofing software for small roofers'],
  title: 'Roofing Estimate Software for Retail Roof Jobs | Scopie', // 54
  metaDescription:
    'Quote roof repairs and replacements by the square, with tear-off, underlayment, flashing and a decking allowance on separate lines, signed before ordering.',
  h1: 'Roofing estimates for repairs and replacements homeowners pay for',
  answerFirst:
    'Scopie is for small roofing companies quoting retail work: repairs and replacements the homeowner pays for directly. Price by the square with tear-off, underlayment, flashing and ventilation on separate lines, include a decking allowance with a per-sheet rate beyond it, offer upgrades as options, and email the estimate for signature.',
  exampleQuote: { label: 'Sample roof replacement estimate', lines, total: '$13,950', depositNote: 'Material deposit and the decking rule are the roofer\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'retail',
      h2: 'Built for the retail side of roofing',
      lead: 'Not every roof is an insurance job. Plenty of homeowners just want a new roof and three honest quotes.',
      paragraphs: [
        'Large roofing platforms are built around claims, adjusters and aerial measurement reports. Many small crews mostly do retail work: leaks, worn-out shingles, a reroof before the house goes on the market. For that work, what wins is a clear estimate that explains the price.',
        'Scopie covers that path: log the call, book the inspection, quote it, get it signed, schedule the crew and the dumpster, and invoice when the last ridge cap is down. It does not do claims or measurements, and this page says so.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A 24-square reroof, line by line',
      label: 'Sample estimate: tear-off and architectural shingles',
      lines,
      total: '$13,950',
      totalLabel: 'Total with the ridge vent upgrade',
      note: 'Example figures only. The ridge vent is optional; the homeowner can sign without it.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Estimate mistakes that cost roofers the margin',
      style: 'mistakes',
      items: [
        { title: 'No decking allowance', text: 'Rot found at tear-off becomes free work unless the estimate sets an allowance and a per-sheet rate.' },
        { title: 'Layers assumed', text: 'Two or three layers of old shingles double the tear-off. Look in the attic or lift an edge before quoting.' },
        { title: 'Flashing and boots hidden in "labor"', text: 'Step flashing, chimney flashing and pipe boots are where leaks start; list them.' },
        { title: 'Ventilation ignored', text: 'Intake and exhaust affect shingle warranties. Say what is being installed.' },
        { title: 'Warranty blurred', text: 'Separate the manufacturer\'s shingle warranty from your workmanship terms.' },
        { title: 'Ordering before signature', text: 'Shingles in a specific color are hard to return. Get the deposit rule signed first.' },
      ],
    },
    {
      type: 'list',
      id: 'questions',
      h2: 'What homeowners ask before signing a roof estimate',
      style: 'bullets',
      items: [
        { text: 'How many days will the roof be open, and what if it rains?' },
        { text: 'Will you replace the rotten boards, and what does that cost?' },
        { text: 'Where does the dumpster go, and will you protect the driveway and beds?' },
        { text: 'Who pulls the permit?' },
        { text: 'What warranty do I get, and from whom?' },
      ],
    },
    {
      type: 'text',
      id: 'season',
      h2: 'Roofing season and the order backlog',
      paragraphs: [
        'Spring storms and fall pre-winter replacements fill calendars, and shingle colors can be back-ordered at the busiest times. A signed estimate with your lead time written on it lets you order materials and book the dumpster with confidence. In Scopie, the signed job goes on the schedule with the crew; crew members see the address and the start time, not the price. When the job is done, the invoice is emailed as a link.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Roofing estimate questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/gutter-cleaning', anchor: 'Scopie for gutter businesses', status: 'live' },
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
  ],
  cta: {
    heading: 'Quote the next roof the day you inspect it',
    text: 'Save your per-square items, flashing and allowances in Scopie, and email each roofing estimate before you leave the driveway.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
